import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Auth } from '~/services/firebase';
import AuthTemplate from '~/layouts/Auth';
import getValidationErrors from '~/utils/getValidationErrors';

interface RecoveryFormData {
  email: string;
  password: string;
}

const Recovery: React.FC = () => {
  const { t } = useTranslation();

  const [data, setData] = useState({} as RecoveryFormData);
  const [errors, setErrors] = useState({} as any);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setLoading(true);
        setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required(t('index.field_required', { field: t('index.email') }))
            .email(t('index.field_invalid', { field: t('index.email') })),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { email } = data;

        await sendPasswordResetEmail(Auth, email);

        setLoading(false);
        setSuccess(t('index.recovery_success'));
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
          return;
        }

        setErrors({
          ...errors,
          message:
            'Não localizamos uma conta com esse e-mail, verifique e tente novamente.',
        });
      }
    },
    [data, t, errors],
  );

  return (
    <AuthTemplate>
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Fatia Prime Pizzaria</h5>
          <div className="nk-block-des">
            <p>Mais vendas, economia e eficiência em seu Delivery.</p>
          </div>
        </div>
      </div>

      {errors.message && (
        <div className="example-alert mb-3">
          <div className="alert alert-danger alert-icon alert-dismissible">
            <em className="icon ni ni-cross-circle" />
            {errors.message}
            <Link to="#close" className="close" data-dismiss="alert" />
          </div>
        </div>
      )}

      {success && (
        <div className="example-alert mb-3">
          <div className="alert alert-success alert-icon alert-dismissible">
            <em className="icon ni ni-cross-circle" />
            {success}
            <Link to="#close" className="close" data-dismiss="alert" />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="default-01">
              {t('index.email')}
            </label>
          </div>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder={t('index.email_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  email: event.target.value,
                });
              }}
            />
            {errors.email && <span className="invalid">{errors.email}</span>}
          </div>
        </div>

        <div className="form-group">
          <button
            type="submit"
            className="btn btn-lg btn-primary btn-block"
            disabled={loading}
          >
            {loading && (
              <div className="spinner-border spinner-border-sm" role="status" />
            )}
            {!loading && t('index.recovery_submit')}
          </button>
        </div>
      </form>
      <div className="form-note-s2 pt-4">
        {t('index.has_account')}
{' '}
<Link to="/">{t('index.signin')}</Link>
      </div>
    </AuthTemplate>
  );
};

export default Recovery;
